import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTournaments, deleteTournament } from '../../api/wikiApi';
import { Trophy, Plus, Pencil, Trash2, Users, Calendar, ExternalLink, RefreshCw } from 'lucide-react';
import styles from './WikiTournamentsManager.module.css';

const STATUS_LABELS = {
  draft: 'Черновик',
  registration: 'Регистрация',
  ongoing: 'В процессе',
  finished: 'Завершён',
  cancelled: 'Отменён',
};

const STATUS_COLORS = {
  draft: 'statusDraft',
  registration: 'statusRegistration',
  ongoing: 'statusOngoing',
  finished: 'statusFinished',
  cancelled: 'statusCancelled',
};

const WikiTournamentsManager = () => {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const loadTournaments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTournaments();
      setTournaments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Ошибка загрузки турниров');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTournaments();
  }, [loadTournaments]);

  const handleDelete = async (id) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      return;
    }
    setDeletingId(id);
    setConfirmDeleteId(null);
    try {
      await deleteTournament(id);
      setTournaments((prev) => prev.filter((t) => String(t.id) !== String(id)));
    } catch (err) {
      setError(err.message || 'Ошибка удаления турнира');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCancelDelete = () => setConfirmDeleteId(null);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (_) {
      return dateStr;
    }
  };

  const getTotalPrize = (prizes) => {
    if (!Array.isArray(prizes) || prizes.length === 0) return null;
    const total = prizes.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    return total > 0 ? total : null;
  };

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <Trophy size={28} className={styles.pageHeaderIcon} />
          <div>
            <h1 className={styles.pageTitle}>Турниры</h1>
            <p className={styles.pageSubtitle}>Управление турнирами и турнирными сетками</p>
          </div>
        </div>
        <div className={styles.pageHeaderActions}>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={loadTournaments}
            disabled={loading}
            title="Обновить"
          >
            <RefreshCw size={18} className={loading ? styles.spinning : ''} />
          </button>
          <button
            type="button"
            className={styles.createButton}
            onClick={() => navigate('/wiki/admin/tournaments/new')}
          >
            <Plus size={18} />
            Создать турнир
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {loading ? (
        <div className={styles.loadingState}>
          <RefreshCw size={32} className={styles.spinning} />
          <span>Загрузка турниров...</span>
        </div>
      ) : tournaments.length === 0 ? (
        <div className={styles.emptyState}>
          <Trophy size={64} className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>Турниров пока нет</h2>
          <p className={styles.emptyText}>Создайте первый турнир, чтобы он появился здесь</p>
          <button
            type="button"
            className={styles.createButton}
            onClick={() => navigate('/wiki/admin/tournaments/new')}
          >
            <Plus size={18} />
            Создать турнир
          </button>
        </div>
      ) : (
        <div className={styles.grid}>
          {tournaments.map((tournament) => {
            const totalPrize = getTotalPrize(tournament.prizes);
            const participantCount = Array.isArray(tournament.participants)
              ? tournament.participants.length
              : (tournament.participantCount || 0);
            const statusKey = tournament.status || 'draft';
            const isDeleting = deletingId === tournament.id;
            const isConfirming = confirmDeleteId === tournament.id;

            return (
              <div key={tournament.id} className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitleRow}>
                    <h3 className={styles.cardTitle}>{tournament.title || 'Без названия'}</h3>
                    <span className={`${styles.statusBadge} ${styles[STATUS_COLORS[statusKey] || 'statusDraft']}`}>
                      {STATUS_LABELS[statusKey] || statusKey}
                    </span>
                  </div>
                  {tournament.description && (
                    <p className={styles.cardDescription}>{tournament.description}</p>
                  )}
                </div>

                <div className={styles.cardMeta}>
                  <div className={styles.metaItem}>
                    <Users size={14} />
                    <span>{participantCount} участников</span>
                  </div>
                  {tournament.startDate && (
                    <div className={styles.metaItem}>
                      <Calendar size={14} />
                      <span>{formatDate(tournament.startDate)}</span>
                    </div>
                  )}
                  {totalPrize !== null && (
                    <div className={styles.metaItem}>
                      <Trophy size={14} />
                      <span>Призовой фонд: {totalPrize.toLocaleString('ru-RU')} ₽</span>
                    </div>
                  )}
                </div>

                {Array.isArray(tournament.rounds) && tournament.rounds.length > 0 && (
                  <div className={styles.roundsInfo}>
                    {tournament.rounds.length} раунд{tournament.rounds.length === 1 ? '' : tournament.rounds.length < 5 ? 'а' : 'ов'}
                  </div>
                )}

                <div className={styles.cardActions}>
                  <a
                    href={`/tournaments/${tournament.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.viewButton}
                    title="Открыть публичную страницу"
                  >
                    <ExternalLink size={15} />
                    Просмотр
                  </a>
                  <button
                    type="button"
                    className={styles.editButton}
                    onClick={() => navigate(`/wiki/admin/tournaments/${tournament.id}`)}
                  >
                    <Pencil size={15} />
                    Редактировать
                  </button>
                  {isConfirming ? (
                    <div className={styles.confirmDelete}>
                      <span>Удалить?</span>
                      <button
                        type="button"
                        className={styles.confirmYes}
                        onClick={() => handleDelete(tournament.id)}
                        disabled={isDeleting}
                      >
                        Да
                      </button>
                      <button
                        type="button"
                        className={styles.confirmNo}
                        onClick={handleCancelDelete}
                      >
                        Нет
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={styles.deleteButton}
                      onClick={() => handleDelete(tournament.id)}
                      disabled={isDeleting}
                      title="Удалить турнир"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WikiTournamentsManager;
